/**
 * The sale window of a ticket type: two offsets in minutes before the start of a
 * timeslot (`ticket_types.sale_opens_before_minutes`, `sale_closes_before_minutes`,
 * NULL = no limit that side, a negative close = after the start). It binds the shop
 * only; tickets handed out from the dashboard (issue_order) ignore it, which is why
 * the dashboard only mentions it. The same sums live in `ticket_type_on_sale`.
 */
export type SaleWindowFields = {
  sale_opens_before_minutes?: number | null;
  sale_closes_before_minutes?: number | null;
};

export type SaleState = 'not_yet' | 'open' | 'closed';

const MINUTE = 60 * 1000;

/** Minutes before `start` that `at` is (negative when after the start). */
export const minutesBefore = (start: string | Date, at: Date) =>
  Math.round((new Date(start).getTime() - at.getTime()) / MINUTE);

/** The moment `minutes` before `start`, or null for no limit. */
export const momentBefore = (start: string | Date, minutes: number | null | undefined) =>
  minutes == null ? null : new Date(new Date(start).getTime() - minutes * MINUTE);

export const saleWindowOf = (tt: SaleWindowFields, start: string | Date) => ({
  opensAt: momentBefore(start, tt.sale_opens_before_minutes),
  closesAt: momentBefore(start, tt.sale_closes_before_minutes),
});

export const saleStateOf = (tt: SaleWindowFields, start: string | Date, now = new Date()): SaleState => {
  const { opensAt, closesAt } = saleWindowOf(tt, start);
  if (opensAt && now < opensAt) return 'not_yet';
  if (closesAt && now >= closesAt) return 'closed';
  return 'open';
};

/**
 * When online sales of a whole timeslot stop, for every ticket type
 * (`event_timeslots.close_before`/`close_before_minutes`): the end of the timeslot,
 * or its start when asked for or when it has no end, minus the minutes. Mirrors
 * `timeslot_sale_closes_at`, which the shop is held to.
 */
export type TimeslotCloseFields = {
  start_date: string | Date;
  end_date?: string | Date | null;
  close_before?: 'start_time' | 'end_time' | null;
  close_before_minutes?: number | null;
};

export const timeslotClosesAt = (ts: TimeslotCloseFields) => {
  const from = (ts.close_before ?? 'end_time') === 'end_time' && ts.end_date ? ts.end_date : ts.start_date;
  return momentBefore(from, ts.close_before_minutes ?? 0)!;
};
